<template>
    <ResponsiveDialog
        :value="value"
        @input="$emit('input', $event)"
        :max-width="420"
        :full-height="false"
        :bottom-inset="54"
        :header-title="$t('journey.officerJenny.title')"
        header-class="skin-window-header--jenny"
        card-class="skin-card"
    >
        <div class="jenny-dialog-bg skin-window--jenny">
            <div class="jenny-dialog-banner"/>
            <div class="jenny-dialog-content">
                <div v-if="foundPokemons.length > 0" class="jenny-dialog-found-row">
                    <v-avatar
                        v-for="p in foundPokemons"
                        :key="p.id"
                        size="56"
                        class="jenny-dialog-found-avatar"
                    >
                        <v-img :src="pokemonIcon(p)" cover/>
                    </v-avatar>
                </div>
                <div class="jenny-dialog-message">{{ message }}</div>
                <div
                    class="jenny-dialog-action-pill"
                    :class="{'jenny-dialog-action-pill--loading': isLoading, 'jenny-dialog-action-pill--disabled': stolenCount === 0}"
                    @click="canInvestigate && investigate()"
                >
                    <v-progress-circular v-if="isLoading" indeterminate size="22" width="3" color="white"/>
                    <template v-else-if="stolenCount === 0">
                        {{ $t('journey.officerJenny.noneMissing') }}
                    </template>
                    <!-- Bez explicitního color propu - zdědí CSS color od pilulky. -->
                    <template v-else>
                        {{ $t('journey.officerJenny.investigation') }}
                        <v-icon size="26" class="ml-1">mdi-white-balance-sunny</v-icon>
                    </template>
                </div>
            </div>
        </div>
        <ConfirmDialog ref="confirmDialog"/>
    </ResponsiveDialog>
</template>

<script>
    // Officer Jenny jako popup nad CITY - `find-stolen-pokemons`
    // (FindStolePokemonsAction.php) nekontroluje position.type vubec, jen
    // cte/zapisuje trenera a position.days, takze dialog nikdy nevola
    // `visit-officer-jenny` (SETTYPE CITY->OFFICER_JENNY), hrac zustava
    // porad v CITY, stejne jako u OakDialog.vue. Design viz
    // claude/components/journey-city.md.
    //
    // `journey/findStonePokemons` (actions.js) mutuje predany `position`
    // primo a znovu ho commitne - `position` prop se proto tady nemutuje
    // primo (eslint vue/no-mutating-props), jen cte.
    import {mapActions} from 'vuex';
    import ResponsiveDialog from '@/components/utils/ResponsiveDialog';
    import ConfirmDialog from '@/components/utils/ConfirmDialog';
    import {pokemonImageSrc} from '@/utils/pokemonImage';

    export default {
        name: 'OfficerJennyDialog',
        components: {ResponsiveDialog, ConfirmDialog},
        props: {
            value: {type: Boolean, default: false},
            trainerId: {type: Number, required: true},
            position: {type: Object, required: true},
        },
        data() {
            return {
                isLoading: false,
                foundPokemons: [],
                apiMessage: null,
            }
        },
        computed: {
            // apiMessage prepisuje investigate() vysledkem z API (viz nize) - dokud neni
            // nastaveny, zprava reaguje na prepnuti jazyka i bez remountu dialogu (CityNext.vue
            // ho drzi porad namountovany, viz komentar u importu vyse).
            message() {
                return this.apiMessage !== null ? this.apiMessage : this.$t('journey.officerJenny.defaultMessage');
            },
            stolenCount() {
                return this.position.trainer.stolenPokemonsCount;
            },
            canInvestigate() {
                return !this.isLoading && this.stolenCount > 0;
            },
        },
        watch: {
            value(isOpen) {
                if (isOpen) {
                    this.foundPokemons = [];
                }
            },
        },
        methods: {
            ...mapActions({
                findStonePokemons: 'journey/findStonePokemons',
            }),
            async investigate() {
                if (!(await this.$refs.confirmDialog.open(this.$t('journey.officerJenny.confirmQuestion')))) {
                    return;
                }
                this.isLoading = true;
                this.findStonePokemons({trainerId: this.trainerId, position: this.position}).then((data) => {
                    this.isLoading = false;
                    if (!data) {
                        return;
                    }
                    this.foundPokemons = data.pokemons || [];
                    this.apiMessage = data.message;
                    if (this.foundPokemons.length > 0) {
                        this.$emit('team-changed');
                    }
                });
            },
            pokemonIcon(p) {
                return pokemonImageSrc(p.pokemonType.img);
            },
        },
    }
</script>

<style scoped>
.jenny-dialog-bg {
    flex: 1 1 auto;
    min-height: 0;
    /* pozadi ridi skin (public/skins/normal.css, .skin-window--jenny) */
    display: flex;
    flex-direction: column;
}
.jenny-dialog-banner {
    flex: none;
    /* ruší flex stretch (cross-axis) - bez tohodle by `width: auto` z
       aspect-ratio níže neměl žádný efekt. */
    align-self: center;
    height: 160px;
    /* skutečný poměr stran Officer_Jenny.png (640x335) - banner má tak
       přesně šířku fotky, žádný prázdný okraj uvnitř rámečku. */
    aspect-ratio: 640 / 335;
    margin: 16px auto 0;
    border-radius: 16px;
    background-image: url('@/assets/pic/web/Officer_Jenny.png');
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center center;
    /* background-color/box-shadow ridi skin (public/skins/normal.css) */
}
.jenny-dialog-content {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
    /* extra místo dole - placeholder pro plovoucí oranžový křížek
       (ResponsiveDialog), stejná hodnota jako CityTravelDialog.vue/
       CityHealDialog.vue/TrainerList.vue. */
    padding: 28px 20px var(--responsive-dialog-close-clearance) 20px;
}
.jenny-dialog-found-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    justify-items: center;
    gap: 12px;
    width: 100%;
}
@media (min-width: 600px) {
    .jenny-dialog-found-row {
        grid-template-columns: repeat(6, 1fr);
    }
}
.jenny-dialog-found-avatar {
    background: #fff;
    border: 3px solid #43A047;
    box-shadow: 1px 2px 5px rgba(0,0,0,0.3);
}
.jenny-dialog-message {
    text-align: center;
    font-size: 15px;
    /* color ridi skin (public/skins/normal.css) */
}
.jenny-dialog-action-pill {
    font-size: 20px;
    font-weight: 700;
    padding: 14px 26px;
    /* zděděno z .skin-card na předkovi (stejný mechanismus jako
       --skin-card-radius v ResponsiveDialog.vue), default = plně kulatá pilulka */
    border-radius: var(--skin-pill-radius, 999px);
    text-align: center;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    min-width: 55%;
    min-height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    /* background/color/box-shadow řídí skin (public/skins/normal.css).
       :active box-shadow je taky ve skin CSS, ne tady - lokální scoped
       pravidlo mělo nižší specificitu než základní pravidlo pro pilulku
       (to používá :not()), takže efekt zmáčknutí se neprojevil. */
}
.jenny-dialog-action-pill--loading {
    pointer-events: none;
    opacity: 0.85;
}
.jenny-dialog-action-pill--disabled {
    pointer-events: none;
    color: #fff;
    background: #BDBDBD;
    box-shadow: 1px 1px 4px 3px rgba(0,0,0,0.25), -1px -2px 6px 3px rgba(0,0,0,0.35) inset;
}
</style>
